import { Core } from "@faire/design-tokens";
import { CapabilitySpecificComponent } from "@faire/retailer-visitor-shared/components/Capabilities/CapabilitySpecificComponent";
import { includesSearchValue } from "@faire/retailer-visitor-shared/views/multi-user/Share/__internal__/util";
import { Avatar } from "@faire/slate/components/avatar";
import { useStrictLocalization } from "@faire/web/common/localization";
import { AddUser } from "@faire/web/slate/icons/foundation/AddUser";
import { Chevron } from "@faire/web/slate/icons/foundation/Chevron";
import { Input, InputWrapper } from "@faire/web/slate/Input";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Tag } from "@faire/web/slate/Tag";
import { Typography } from "@faire/web/slate/Typography";
import ClickOutside from "@faire/web/ui/ClickOutside";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { trackRetailerTeamManagementInviteTeamMemberDropdownItemClick } from "@faire/web-api--source/events/retailerTeamManagement/click/inviteTeamMemberDropdownItem";
import { RETAILER_INVITE_ACCOUNT_USER } from "@faire/web-api--source/faire/session/CapabilityEnum/RETAILER_INVITE_ACCOUNT_USER";
import { IRetailerShareableUser } from "@faire/web-api--source/indigofair/retailer/IRetailerShareableUser";
import React, { useState } from "react";
import styled from "styled-components";

interface IProps {
  options: Array<IRetailerShareableUser>;
  selectedUsers: Array<IRetailerShareableUser>;
  onSelect: (options: Array<IRetailerShareableUser>) => void;
  pageName: string;
  onInviteButtonClick?: () => void;
  errorMessage: string | undefined;
  setErrorMessage: (errorMessage: string | undefined) => void;
}

export const SelectTeamUsersDropdown: React.FC<IProps> = ({
  options,
  selectedUsers,
  onSelect,
  pageName,
  onInviteButtonClick,
  errorMessage,
  setErrorMessage,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const filteredUserOptions = options.filter(
    (option) =>
      !selectedUsers
        .flatMap((o) => o.user_token)
        .includes(option.user_token ?? "") &&
      (includesSearchValue(option.full_name ?? "", searchValue) ||
        includesSearchValue(option.email ?? "", searchValue))
  );

  const showNoResultsSection = filteredUserOptions.length === 0 && searchValue;

  const handleInviteButtonClick = () => {
    trackRetailerTeamManagementInviteTeamMemberDropdownItemClick(pageName);
    onInviteButtonClick?.();
  };

  const handleCloseDropdown = () => {
    setSearchValue("");
    setIsDropdownOpen(false);
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (isDropdownOpen) {
      handleCloseDropdown();
    } else {
      setIsDropdownOpen(true);
    }
    e.preventDefault();
    e.stopPropagation();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDropdownOpen(true);
    setSearchValue(e.target.value);
  };

  const handleSelectValue = (value: IRetailerShareableUser) => () => {
    setErrorMessage(undefined);
    if (
      selectedUsers
        .flatMap((user) => user.user_token)
        .includes(value.user_token)
    ) {
      // remove user
      onSelect(selectedUsers.filter((v) => v.user_token !== value.user_token));
    } else {
      // add user
      onSelect([...selectedUsers, value]);
      handleCloseDropdown();
    }
  };

  return (
    <Column>
      <ClickOutside onClickOutside={handleCloseDropdown}>
        <Wrapper>
          <Input
            label={strictLocalize({
              defaultMessage: "Team member(s)",
              description: {
                text: "label to describe the input field that lets user select a team member",
                img: "https://cdn.faire.com/fastly/7a3c6620150b24dc7d4b2591585322de8eef974239065d7b1af41051c9878990.png",
              },
            })}
            data-test-id="select-team-users-dropdown-input"
            placeholder={
              isDropdownOpen || errorMessage
                ? strictLocalize({
                    defaultMessage: "Select team member",
                    description: {
                      text: "text for label that lets user know that they can select a team member",
                      img: "https://cdn.faire.com/fastly/7a3c6620150b24dc7d4b2591585322de8eef974239065d7b1af41051c9878990.png",
                    },
                  })
                : undefined
            }
            value={searchValue}
            onChange={handleInputChange}
            onClick={handleInputClick}
            error={errorMessage}
          />
          {isDropdownOpen ? (
            <Dropdown>
              <DropdownResults
                align="center"
                data-test-id="select-team-users-dropdown"
              >
                {filteredUserOptions.map((user) => (
                  <DropdownItem
                    key={user.user_token}
                    onClick={handleSelectValue(user)}
                  >
                    <Avatar image={user.avatar} size="xSmall" />
                    <Spacer size="2x" />
                    <Column style={{ maxWidth: "70%" }}>
                      <Typography variant="paragraphSansMedium" truncate>
                        {user.full_name}
                      </Typography>
                      <Typography truncate>{user.email}</Typography>
                    </Column>
                  </DropdownItem>
                ))}
              </DropdownResults>
              {showNoResultsSection ? (
                <>
                  <DividerLine />
                  <NonSelectableDropdownItem>
                    <Typography color={Core.text.subdued}>
                      <StrictLocalMsg
                        defaultMessage="No results"
                        description={{
                          text: "text for label that lets user know that no results were found with the search term",
                          img: "https://cdn.faire.com/fastly/4728a62df074aab43301a95f91d89f7d3a83e458e51f5432ea825f47e585df1e.png",
                        }}
                      />
                    </Typography>
                  </NonSelectableDropdownItem>
                </>
              ) : null}
              <CapabilitySpecificComponent
                capability={RETAILER_INVITE_ACCOUNT_USER}
                errorComponent={null}
              >
                <DividerLine />
                <DropdownItem>
                  <InviteUserWrapper
                    justify="space-between"
                    align="center"
                    onClick={handleInviteButtonClick}
                  >
                    <Row align="center">
                      <AddUser strokeWidth={2} fontSize="16px" />
                      <Spacer size="2x" />
                      <Typography variant="paragraphSansMedium">
                        <StrictLocalMsg
                          defaultMessage="Invite a team member"
                          description={{
                            text: "text for label that lets user know that they can invite a team member",
                          }}
                        />
                      </Typography>
                    </Row>
                    <Chevron fontSize="16px" direction="right" />
                  </InviteUserWrapper>
                </DropdownItem>
              </CapabilitySpecificComponent>
            </Dropdown>
          ) : null}
        </Wrapper>
      </ClickOutside>
      <Spacer height="1x" />
      <SelectedUsers gap={4}>
        {selectedUsers.map((value) => (
          <Tag
            key={value.user_token}
            label={value.full_name ?? ""}
            onClose={handleSelectValue(value)}
            data-test-id={`${value.user_token}-tag`}
          />
        ))}
      </SelectedUsers>
    </Column>
  );
};

const Wrapper = styled(Column)`
  position: relative;

  ${InputWrapper} {
    cursor: pointer;
  }
`;

const Dropdown = styled(Column)`
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  position: absolute;
  transform: translateY(100%);
  background-color: ${Core.surface.inverse};
  border: 1px solid ${Core.border.muted};
  border-top: 0;
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- false alarm
const DropdownResults = styled(Column)`
  max-height: 192px;
  overflow-y: auto;

  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const DropdownItem = styled(Row).attrs({ align: "center" })`
  padding: ${getSpacing("1x", "2x")};
  width: 100%;
  cursor: pointer;
  min-height: 64px;
  background-color: ${Core.surface.inverse};

  &:hover {
    background-color: ${Core.surface.subdued};
  }
`;

const NonSelectableDropdownItem = styled(DropdownItem)`
  cursor: default;

  &:hover {
    background-color: ${Core.surface.inverse};
  }
`;

const InviteUserWrapper = styled(Row)`
  width: 100%;
`;

// eslint-disable-next-line @faire/prefer-slate-attrs -- false alarm
const SelectedUsers = styled(Row)`
  flex-wrap: wrap;
  max-height: 110px;
  overflow-y: auto;
  padding: ${getSpacing("1x", 0)};

  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const DividerLine = styled.div`
  position: relative;
  height: 0;
  width: 100%;
  border-top: 1px solid ${Core.border.muted};
`;
